/**
 * The browser's half of an admin's passkey (step 90): WebAuthn's two ceremonies, with the options the API
 * sends (JSON, base64url) turned into what `navigator.credentials` takes, and its answer back into JSON.
 * No library — the conversion is the whole of it. The API verifies; nothing here decides.
 */
import { readNativeApp } from '@myrave/shared';
import { inNativeApp, passkeyInApp } from './nativeBridge';

const fromB64url = (s: string): ArrayBuffer => {
  const b = atob(s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4));
  const out = new Uint8Array(b.length);
  for (let i = 0; i < b.length; i++) out[i] = b.charCodeAt(i);
  return out.buffer;
};
const toB64url = (buf: ArrayBuffer | null | undefined): string | undefined => {
  if (!buf) return undefined;
  let s = '';
  for (const byte of new Uint8Array(buf)) s += String.fromCharCode(byte);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

type Descriptor = { id: string; type?: string; transports?: string[] };
export interface CreationOptionsJSON {
  challenge: string; rp: { id?: string; name: string }; user: { id: string; name: string; displayName: string };
  pubKeyCredParams: { type: string; alg: number }[]; timeout?: number; excludeCredentials?: Descriptor[];
  authenticatorSelection?: Record<string, unknown>; attestation?: string;
}
export interface RequestOptionsJSON {
  challenge: string; rpId?: string; timeout?: number; allowCredentials?: Descriptor[]; userVerification?: string;
}

const descriptors = (list?: Descriptor[]) => (list ?? []).map((d) => ({
  id: fromB64url(d.id), type: 'public-key' as const, ...(d.transports ? { transports: d.transports as AuthenticatorTransport[] } : {}),
}));

/**
 * The Android app with the passkey bridge (2026-10-30): its web view has no WebAuthn, so the ceremony goes
 * to the shell — Android's Credential Manager, which also offers a passkey on another device by QR code.
 */
function androidBridge(): boolean {
  if (typeof navigator === 'undefined') return false;
  const app = readNativeApp(navigator.userAgent);
  return app?.os === 'android' && app.passkeys && inNativeApp();
}

/** Can this page make and use a passkey at all? (Android's web view only through the app's bridge.) */
export function passkeysAvailable(): boolean {
  if (androidBridge()) return true;
  if (typeof window === 'undefined' || typeof window.PublicKeyCredential !== 'function'
    || typeof navigator.credentials?.create !== 'function') return false;
  // Inside the phone app the web view has the API but may not be allowed to use it (2026-10-29: an
  // iPhone build without `webcredentials:` refused every passkey — "Try again" for ever). Only a
  // shell that says it can is offered one.
  const app = readNativeApp(navigator.userAgent);
  return app === null || app.passkeys;
}

/** In the iPhone app, but a build that cannot use passkeys — the one place "update the app" helps. */
export function passkeysNeedNewerApp(): boolean {
  const app = typeof navigator === 'undefined' ? null : readNativeApp(navigator.userAgent);
  return app !== null && app.os === 'ios' && !app.passkeys;
}

export async function createPasskey(o: CreationOptionsJSON): Promise<Record<string, unknown>> {
  // The Android app: the API's options as they came, and Credential Manager's JSON back as the API's answer.
  if (androidBridge()) return passkeyInApp('create', o);
  const cred = await navigator.credentials.create({
    publicKey: {
      challenge: fromB64url(o.challenge), rp: o.rp,
      user: { id: fromB64url(o.user.id), name: o.user.name, displayName: o.user.displayName },
      pubKeyCredParams: o.pubKeyCredParams as PublicKeyCredentialParameters[], timeout: o.timeout,
      excludeCredentials: descriptors(o.excludeCredentials),
      authenticatorSelection: o.authenticatorSelection as AuthenticatorSelectionCriteria | undefined,
      attestation: (o.attestation ?? 'none') as AttestationConveyancePreference,
    },
  }) as PublicKeyCredential | null;
  if (!cred) throw new Error('no passkey');
  const r = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id, rawId: toB64url(cred.rawId), type: cred.type, clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: toB64url(r.clientDataJSON), attestationObject: toB64url(r.attestationObject),
      transports: typeof r.getTransports === 'function' ? r.getTransports() : [],
    },
  };
}

export async function usePasskey(o: RequestOptionsJSON): Promise<Record<string, unknown>> {
  if (androidBridge()) return passkeyInApp('get', o);
  const cred = await navigator.credentials.get({
    publicKey: {
      challenge: fromB64url(o.challenge), rpId: o.rpId, timeout: o.timeout,
      allowCredentials: descriptors(o.allowCredentials),
      userVerification: (o.userVerification ?? 'preferred') as UserVerificationRequirement,
    },
  }) as PublicKeyCredential | null;
  if (!cred) throw new Error('no passkey');
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id, rawId: toB64url(cred.rawId), type: cred.type, clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: toB64url(r.clientDataJSON), authenticatorData: toB64url(r.authenticatorData),
      signature: toB64url(r.signature), userHandle: toB64url(r.userHandle),
    },
  };
}

/** A name for a new passkey that says which device it is on ("Mac", "iPhone", "Android", "Windows"). */
export function deviceName(): string {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  return /iPhone/.test(ua) ? 'iPhone' : /iPad/.test(ua) ? 'iPad' : /Android/.test(ua) ? 'Android'
    : /Mac OS X/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : 'Passkey';
}
